import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Switch,
  InputNumber,
  Space,
  message,
  Popconfirm,
  Tag,
  Typography,
  Divider,
  Row,
  Col
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SettingOutlined
} from '@ant-design/icons';
import { serviceService, ServiceParam, CreateServiceParamRequest, UpdateServiceParamRequest } from '../services/serviceService';

const { Title, Text } = Typography;
const { Option } = Select;

interface ServiceParamManagerProps {
  serviceId: number;
  serviceName: string;
  onClose?: () => void;
}

const ServiceParamManager: React.FC<ServiceParamManagerProps> = ({
  serviceId,
  serviceName,
  onClose
}) => {
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();
  
  const [loading, setLoading] = useState(false);
  const [params, setParams] = useState<ServiceParam[]>([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [editingParam, setEditingParam] = useState<ServiceParam | null>(null);

  // 加载服务参数
  const loadParams = async () => {
    setLoading(true);
    try {
      const data = await serviceService.getServiceParams(serviceId);
      setParams(data);
    } catch (error: any) {
      message.error(error.message || '加载服务参数失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadParams();
  }, [serviceId]);

  // 创建参数
  const handleCreate = async (values: CreateServiceParamRequest) => {
    try {
      await serviceService.createServiceParam(serviceId, {
        ...values,
        service_id: serviceId
      });
      message.success('参数创建成功');
      setIsModalVisible(false);
      form.resetFields();
      loadParams();
    } catch (error: any) {
      message.error(error.message || '创建参数失败');
    }
  };

  // 编辑参数
  const handleEdit = (param: ServiceParam) => {
    setEditingParam(param);
    editForm.setFieldsValue({
      param_name: param.param_name,
      param_value: param.param_value,
      param_type: param.param_type,
      description: param.description,
      is_required: param.is_required,
      sort_order: param.sort_order
    });
    setIsEditModalVisible(true);
  };

  // 更新参数
  const handleUpdate = async (values: UpdateServiceParamRequest) => {
    if (!editingParam) return;
    
    try {
      await serviceService.updateServiceParam(serviceId, editingParam.id, values);
      message.success('参数更新成功');
      setIsEditModalVisible(false);
      setEditingParam(null);
      editForm.resetFields();
      loadParams();
    } catch (error: any) {
      message.error(error.message || '更新参数失败');
    }
  };

  // 删除参数
  const handleDelete = async (paramId: number) => {
    try {
      await serviceService.deleteServiceParam(serviceId, paramId);
      message.success('参数删除成功');
      loadParams();
    } catch (error: any) {
      message.error(error.message || '删除参数失败');
    }
  };

  const columns = [
    {
      title: '参数名称',
      dataIndex: 'param_name',
      key: 'param_name',
      width: 150,
      render: (text: string, record: ServiceParam) => (
        <Space>
          <Text strong>{text}</Text>
          {record.is_required && <Tag color="red">必填</Tag>}
        </Space>
      )
    },
    {
      title: '参数值',
      dataIndex: 'param_value',
      key: 'param_value',
      width: 200,
      ellipsis: true,
      render: (text: string, record: ServiceParam) => {
        if (record.param_type === 'boolean') {
          return <Tag color={text === 'true' ? 'green' : 'red'}>{text === 'true' ? '是' : '否'}</Tag>;
        }
        if (record.param_type === 'json') {
          return <Text code>{text ? 'JSON数据' : '空'}</Text>;
        }
        return <Text>{text || '-'}</Text>;
      }
    },
    {
      title: '类型',
      dataIndex: 'param_type',
      key: 'param_type',
      width: 100,
      render: (type: string) => {
        const colorMap: Record<string, string> = {
          string: 'blue',
          number: 'green',
          boolean: 'orange',
          json: 'purple'
        };
        return <Tag color={colorMap[type] || 'default'}>{type}</Tag>;
      }
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      render: (text: string) => <Text type="secondary">{text || '-'}</Text>
    },
    {
      title: '排序',
      dataIndex: 'sort_order',
      key: 'sort_order',
      width: 80,
      sorter: (a: ServiceParam, b: ServiceParam) => a.sort_order - b.sort_order
    },
    {
      title: '操作',
      key: 'actions',
      width: 120,
      render: (record: ServiceParam) => (
        <Space>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            size="small"
          />
          <Popconfirm
            title="确定要删除这个参数吗？"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              size="small"
            />
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <Card 
      title={
        <Space>
          <SettingOutlined />
          <span>服务参数管理</span>
          <Text type="secondary">({serviceName})</Text>
        </Space>
      }
      extra={
        <Space>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setIsModalVisible(true)}
          >
            添加参数
          </Button>
          {onClose && (
            <Button onClick={onClose}>
              关闭
            </Button>
          )}
        </Space>
      }
      size="small"
    >
      <Table
        columns={columns}
        dataSource={params}
        rowKey="id"
        loading={loading}
        size="small"
        pagination={{
          pageSize: 10,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`
        }}
        scroll={{ y: 400 }}
      />

      {/* 创建参数模态框 */}
      <Modal
        title="添加服务参数"
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          form.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleCreate}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="param_name"
                label="参数名称"
                rules={[{ required: true, message: '请输入参数名称' }]}
              >
                <Input placeholder="参数名称" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="param_type"
                label="参数类型"
                rules={[{ required: true, message: '请选择参数类型' }]}
                initialValue="string"
              >
                <Select placeholder="选择参数类型">
                  <Option value="string">字符串</Option>
                  <Option value="number">数字</Option>
                  <Option value="boolean">布尔值</Option>
                  <Option value="json">JSON</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="param_value"
            label="参数值"
          >
            <Input.TextArea 
              placeholder="参数值" 
              rows={3}
            />
          </Form.Item>

          <Form.Item
            name="description"
            label="描述"
          >
            <Input.TextArea 
              placeholder="参数描述" 
              rows={2}
            />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="is_required"
                label="是否必填"
                valuePropName="checked"
                initialValue={false}
              >
                <Switch />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="sort_order"
                label="排序"
                initialValue={0}
              >
                <InputNumber 
                  min={0} 
                  placeholder="排序" 
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                创建
              </Button>
              <Button onClick={() => {
                setIsModalVisible(false);
                form.resetFields();
              }}>
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 编辑参数模态框 */}
      <Modal
        title="编辑服务参数"
        open={isEditModalVisible}
        onCancel={() => {
          setIsEditModalVisible(false);
          setEditingParam(null);
          editForm.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form
          form={editForm}
          layout="vertical"
          onFinish={handleUpdate}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="param_name"
                label="参数名称"
                rules={[{ required: true, message: '请输入参数名称' }]}
              >
                <Input placeholder="参数名称" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="param_type"
                label="参数类型"
                rules={[{ required: true, message: '请选择参数类型' }]}
              >
                <Select placeholder="选择参数类型">
                  <Option value="string">字符串</Option>
                  <Option value="number">数字</Option>
                  <Option value="boolean">布尔值</Option>
                  <Option value="json">JSON</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="param_value"
            label="参数值"
          >
            <Input.TextArea 
              placeholder="参数值" 
              rows={3}
            />
          </Form.Item>

          <Form.Item
            name="description"
            label="描述"
          >
            <Input.TextArea 
              placeholder="参数描述" 
              rows={2}
            />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="is_required"
                label="是否必填"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="sort_order"
                label="排序"
              >
                <InputNumber 
                  min={0} 
                  placeholder="排序" 
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                更新
              </Button>
              <Button onClick={() => {
                setIsEditModalVisible(false);
                setEditingParam(null);
                editForm.resetFields();
              }}>
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default ServiceParamManager;

